import React, { useRef, useLayoutEffect, useState } from "react";
import PropTypes from 'prop-types';

const measureText = (text, fontSize = 12, fontWeight = 600, fontFamily = "Noto Sans, sans-serif") => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
    return ctx.measureText(text).width;
};

const MIN_ARROW_WIDTH = 80;
const H_PADDING = 24;
const NOTCH = 10;

const computeRows = (stages, containerWidth) => {
    if (!containerWidth || !stages.length) return [];

    const rows = [];
    let currentRow = [];
    let currentRowWidth = 0;

    stages.forEach((stage, i) => {
        const labelWidth = Math.max(
            measureText(stage.option) + H_PADDING * 2 + NOTCH,
            MIN_ARROW_WIDTH
        );

        if (currentRowWidth + labelWidth > containerWidth + 1 && currentRow.length > 0) {
            rows.push(currentRow);
            currentRow = [];
            currentRowWidth = 0;
        }

        currentRow.push({
            ...stage,
            measuredWidth: labelWidth,
            originalIndex: i,
        });
        currentRowWidth += labelWidth;
    });

    if (currentRow.length) rows.push(currentRow);

    return rows.map((row, rowIndex) => {
        const isLastRow = rowIndex === rows.length - 1;
        const isSingleItem = row.length === 1;

        if (isLastRow && isSingleItem) {
            return row.map((s) => ({ ...s, arrowWidth: s.measuredWidth }));
        }

        const totalMeasured = row.reduce((sum, s) => sum + s.measuredWidth, 0);
        const scale = containerWidth / totalMeasured;
        return row.map((s) => ({ ...s, arrowWidth: s.measuredWidth * scale }));
    });
};

const ArrowPipeline = ({
    stages,
    currentStage,
    onStageClick,
    completedColor = "#0d6efd",
    upcomingColor = "#d3d3d3",
    arrowHeight = 40,
    disabledItem,
    UpdatePs,
    canUpdateDelete,
    displayData,
}) => {
    const containerRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);
    // hoveredOriginalIndex: the originalIndex of the arrow being hovered
    const [hoveredOriginalIndex, setHoveredOriginalIndex] = useState(null);

    useLayoutEffect(() => {
        if (!containerRef.current) return;
        const ro = new ResizeObserver(() => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth);
            }
        });
        ro.observe(containerRef.current);
        setContainerWidth(containerRef.current.offsetWidth);
        return () => ro.disconnect();
    }, []);

    const rows = computeRows(stages, containerWidth);
    const totalStages = stages.length;

    const canEdit = canUpdateDelete(
        UpdatePs.permission,
        UpdatePs.dataPermission,
        UpdatePs.type,
        UpdatePs.PId
    );
    const isApprovedNone = displayData?.approved_for === "None";
    const isInteractive = canEdit && isApprovedNone;

    const rowGap = 8;
    const totalHeight = rows.length > 0
        ? rows.length * arrowHeight + (rows.length - 1) * rowGap
        : arrowHeight;

    // The "active ceiling" — highest originalIndex that should be filled blue.
    // On hover: fill up to hovered index. Otherwise: fill up to currentStage index.
    const activeCeiling = hoveredOriginalIndex !== null
        ? hoveredOriginalIndex
        : (currentStage != null ? stages.findIndex(s => s.customDropdownOptionId === currentStage) : -1);

    return (
        <div ref={containerRef} style={{ width: "100%" }}>
            <svg
                height={totalHeight}
                width={containerWidth || "100%"}
                style={{ fontFamily: "'Noto Sans', sans-serif", display: "block" }}
                onMouseLeave={() => setHoveredOriginalIndex(null)}
            >
                {rows.map((row, rowIndex) => {
                    const rowY = rowIndex * (arrowHeight + rowGap);
                    let xCursor = 0;

                    return (
                        <g key={rowIndex} transform={`translate(0, ${rowY})`}>
                            {row.map((stage, colIndex) => {
                                const { customDropdownOptionId, option, arrowWidth, originalIndex } = stage;

                                const isGlobalFirst = originalIndex === 0;
                                const isGlobalLast = originalIndex === totalStages - 1;
                                // const isFirstInRow = colIndex === 0;
                                const isLastInRow = colIndex === row.length - 1;

                                const x = xCursor;
                                xCursor += arrowWidth;

                                // Flat left only for the very first arrow globally;
                                // all other row-starts get a notch on the left.
                                const flatLeft = isGlobalFirst;
                                // Flat right only for the very last arrow globally.
                                const flatRight = isGlobalLast && isLastInRow;

                                const w = arrowWidth;

                                let points;
                                if (flatLeft && flatRight) {
                                    // Only item, and it IS the last — plain rectangle
                                    points = `${x},0 ${x + w},0 ${x + w},${arrowHeight} ${x},${arrowHeight}`;
                                } else if (flatLeft) {
                                    // First item globally — flat left, notch right
                                    points = `${x},0 ${x + w - NOTCH},0 ${x + w},${arrowHeight / 2} ${x + w - NOTCH},${arrowHeight} ${x},${arrowHeight}`;
                                } else if (flatRight) {
                                    // Last item globally — notch left, flat right
                                    points = `${x},0 ${x + w},0 ${x + w},${arrowHeight} ${x},${arrowHeight} ${x + NOTCH},${arrowHeight / 2}`;
                                } else {
                                    // Everything else — notch both sides
                                    points = `${x},0 ${x + w - NOTCH},0 ${x + w},${arrowHeight / 2} ${x + w - NOTCH},${arrowHeight} ${x},${arrowHeight} ${x + NOTCH},${arrowHeight / 2}`;
                                }

                                const isFilled = isInteractive && originalIndex <= activeCeiling;
                                const fillColor = isFilled ? completedColor : upcomingColor;
                                const textFill = isFilled ? "#ffffff" : "#a1a1a1";

                                const textX = x + arrowWidth / 2;
                                const words = option.split(" ");
                                let textContent;
                                if (words.length <= 2) {
                                    textContent = <tspan>{words.join(" ")}</tspan>;
                                } else {
                                    const mid = Math.ceil(words.length / 2);
                                    textContent = (
                                        <>
                                            <tspan x={textX} dy="-5">{words.slice(0, mid).join(" ")}</tspan>
                                            <tspan x={textX} dy="14">{words.slice(mid).join(" ")}</tspan>
                                        </>
                                    );
                                }

                                return (
                                    <g
                                        key={customDropdownOptionId}
                                        onClick={() => {
                                            if (isInteractive && !disabledItem) {
                                                onStageClick(customDropdownOptionId);
                                            }
                                        }}
                                        onMouseEnter={() => {
                                            if (isInteractive) setHoveredOriginalIndex(originalIndex);
                                        }}
                                        style={{
                                            cursor: isInteractive && !disabledItem ? "pointer" : "default",
                                        }}
                                    >
                                        <polygon points={points} fill={fillColor} />
                                        <text
                                            x={textX}
                                            y={arrowHeight / 2}
                                            textAnchor="middle"
                                            fill={textFill}
                                            fontSize="12"
                                            fontWeight="600"
                                            dominantBaseline="middle"
                                        >
                                            {textContent}
                                        </text>
                                    </g>
                                );
                            })}
                        </g>
                    );
                })}
            </svg>
        </div>
    );
};

ArrowPipeline.propTypes = {
    stages: PropTypes.array.isRequired,
    currentStage: PropTypes.number,
    onStageClick: PropTypes.func,
    completedColor: PropTypes.string,
    upcomingColor: PropTypes.string,
    arrowHeight: PropTypes.number,
    disabledItem: PropTypes.bool,
    UpdatePs: PropTypes.object.isRequired,
    displayData: PropTypes.object.isRequired,
    canUpdateDelete: PropTypes.func.isRequired,
};

export default ArrowPipeline;